import { z } from "zod";

import { SmsConversationStatus } from "@/src/models/comms/sms-conversation";
import { DatabaseDefaults } from "@/src/models/database-defaults";

/**
 * What kind of change an event records. Status transitions drive the lifecycle
 * and metrics; agent_toggle / assignment / scheduling_outcome events carry their
 * payload in metadata (with toStatus === fromStatus, so they leave the status
 * projection untouched) so the audit trail — who turned the agent off, who
 * reassigned, which appointment mutation succeeded — is queryable.
 */
export enum SmsConversationEventKind {
  STATUS = "status",
  AGENT_TOGGLE = "agent_toggle",
  ASSIGNMENT = "assignment",
  SCHEDULING_OUTCOME = "scheduling_outcome",
}

/** Who produced the event. */
export enum SmsConversationActorType {
  AGENT = "agent",
  USER = "user",
}

export const smsConversationEventSchema = z.object({
  conversationId: z.string().uuid(),
  // Denormalized from the conversation for metric grouping.
  patientId: z.string().uuid(),
  eventKind: z.nativeEnum(SmsConversationEventKind).nullable(),
  // Prior status; null on the initial OPENED event.
  fromStatus: z.nativeEnum(SmsConversationStatus).nullable(),
  toStatus: z.nativeEnum(SmsConversationStatus),
  // Interaction intent (e.g. "scheduling" | "billing" | "cancellation").
  intent: z.string().nullable(),
  // The workflow this event belongs to; null for intent-less events (agent_toggle / assignment) and historical rows.
  workflowId: z.string().uuid().nullable(),
  // escalation_reason / closed_reason / "intent_switch" / ...
  reason: z.string().nullable(),
  // Message that triggered this event; null for system/UI events.
  messageId: z.string().uuid().nullable(),
  actorType: z.nativeEnum(SmsConversationActorType),
  // Staff user, when actorType === "user".
  actorUserId: z.string().uuid().nullable(),
  // Free-form per-event attributes (e.g. { agentEnabled: false } for agent_toggle events).
  metadata: z.record(z.string(), z.any()),
});

export type SmsConversationEvent = z.infer<typeof smsConversationEventSchema> & DatabaseDefaults;

export const smsConversationEventShape: SmsConversationEvent = {
  id: "",
  groupId: "",
  conversationId: "",
  patientId: "",
  eventKind: null,
  fromStatus: null,
  toStatus: SmsConversationStatus.PROCESSING,
  intent: null,
  workflowId: null,
  reason: null,
  messageId: null,
  actorType: SmsConversationActorType.AGENT,
  actorUserId: null,
  metadata: {},
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};
